/**
 * The cast, redrawn in the shared art system:
 *  - Scribble: a sitting fox kit whose tail tip looks dipped in blue paint (the mascot).
 *  - Teacher Juno: a young illustrator, bust view, hair bun with a pencil, round glasses.
 * Same construction rules as every lesson subject: big head, round shapes, one silhouette.
 */
import type { FigureDef, Layer } from '../Figure';
import { ellipse, mirrorX, poly } from '../geom';
import type { StyleId } from '../tokens';

const M = (d: string) => mirrorX(d, 120);
const eyes = (cx: number, cy: number, rx: number, ry: number): Layer[] => [
  { k: 'spot', d: ellipse(cx, cy, rx, ry), fill: 'eye', stage: 3 },
  { k: 'spot', d: ellipse(cx + rx * 0.32, cy - ry * 0.38, rx * 0.4, rx * 0.4), fill: 'white', stage: 3 },
  { k: 'spot', d: ellipse(cx - rx * 0.34, cy + ry * 0.42, rx * 0.17, rx * 0.17), fill: 'white', stage: 3 },
];

/* --------------------------------- Scribble --------------------------------- */

const F_HEAD = 'M120 50C150 50 170 64 178 86C184 100 191 112 198 121C185 126 174 132 162 139C150 146 135 150 120 150C105 150 90 146 78 139C66 132 55 126 42 121C49 112 56 100 62 86C70 64 90 50 120 50Z';
const F_EAR = 'M66 92C59 68 59 40 66 20C69 14 75 13 80 17C92 30 103 43 111 55Z';
const F_EAR_IN = 'M73 66C71 52 71 39 73 29C81 37 89 46 95 54Z';
const F_MASK = 'M42 121C55 125 66 132 78 139C90 146 105 150 120 150C135 150 150 146 162 139C174 132 185 125 198 121C188 115 172 109 157 110C141 111 129 117 120 127C111 117 99 111 83 110C68 109 52 115 42 121Z';
const F_BODY = 'M120 132C147 132 162 152 166 178C170 199 164 214 148 219L92 219C76 214 70 199 74 178C78 152 93 132 120 132Z';
const F_BIB = 'M120 148C133 148 141 161 141 176C141 189 133 198 120 203C107 198 99 189 99 176C99 161 107 148 120 148Z';
const F_TAIL = 'M154 214C190 222 224 202 224 166C224 139 207 120 190 124C175 128 176 149 181 165C186 182 175 196 158 197Z';
const F_TIP = 'M160 96L240 96L240 150C231 145 225 158 213 154C203 151 197 161 186 155C181 152 176 150 170 151L160 151Z';
const F_PAW = 'M87 216C87 208 94 204 102 204C110 204 117 208 117 216C117 222 110 225 102 225C94 225 87 222 87 216Z';
const F_SCARF = 'M86 141C104 151 136 151 154 141L156 150C147 156 137 159 128 160L131 176L116 171L109 159C101 157 93 154 85 150Z';

export function scribble(style: StyleId): FigureDef {
  const kawaii = style === 'C';
  const ey = kawaii ? 106 : 102;
  return {
    vb: [240, 240],
    ground: [124, 222, 84, 9],
    layers: [
      { k: 'region', d: F_TAIL, fill: 'foxFur', sil: true, rim: true, shade: 9 },
      { k: 'region', d: F_TIP, fill: 'foxTip', clip: F_TAIL, stage: 3, shade: 6 },
      { k: 'ink', d: 'M196 186C201 182 205 178 208 172', w: 0.8, clip: F_TAIL },
      { k: 'region', d: F_BODY, fill: 'foxFur', sil: true, rim: true, shade: 14 },
      { k: 'region', d: F_BIB, fill: 'cream', stage: 3, shade: 6, line: style !== 'B' },
      { k: 'ink', d: 'M77 196C79 179 91 171 103 183', w: 0.9 },
      { k: 'ink', d: M('M77 196C79 179 91 171 103 183'), w: 0.9 },
      { k: 'spot', d: ellipse(120, 147, 50, 13), fill: 'shade', clip: F_BODY },
      { k: 'region', d: F_PAW, fill: 'foxDark', sil: true, shade: 4 },
      { k: 'region', d: M(F_PAW), fill: 'foxDark', sil: true, shade: 4 },
      { k: 'region', d: F_SCARF, fill: 'scarf', stage: 3, shade: 4, rim: true },
      { k: 'ink', d: 'M121 161L124 171', w: 0.5, taper: 'none' },
      { k: 'region', d: F_EAR, fill: 'foxFur', sil: true, rim: true, shade: 6 },
      { k: 'region', d: M(F_EAR), fill: 'foxFur', sil: true, shade: 6 },
      { k: 'region', d: F_EAR_IN, fill: 'foxDark', stage: 3, line: false, shade: false },
      { k: 'region', d: M(F_EAR_IN), fill: 'foxDark', stage: 3, line: false, shade: false },
      { k: 'region', d: F_HEAD, fill: 'foxFur', sil: true, rim: true, shade: 11 },
      { k: 'region', d: F_MASK, fill: 'cream', clip: F_HEAD, stage: 3, shade: 6 },
      { k: 'ink', d: 'M88 80C93 77 99 77 104 79', w: 0.9, color: 'foxDark' },
      { k: 'ink', d: M('M88 80C93 77 99 77 104 79'), w: 0.9, color: 'foxDark' },
      ...eyes(98, ey, kawaii ? 8 : 8.5, kawaii ? 9 : 11),
      ...eyes(142, ey, kawaii ? 8 : 8.5, kawaii ? 9 : 11),
      { k: 'spot', d: ellipse(80, 120, kawaii ? 10 : 8, kawaii ? 6 : 5), fill: 'blush', blush: true, opacity: 0.55 },
      { k: 'spot', d: ellipse(160, 120, kawaii ? 10 : 8, kawaii ? 6 : 5), fill: 'blush', blush: true, opacity: 0.55 },
      { k: 'region', d: 'M112 126C112 122 128 122 128 126C128 131 123 134 120 134C117 134 112 131 112 126Z', fill: 'foxDark', stage: 3, shade: false, line: false },
      { k: 'spot', d: ellipse(117, 125.5, 2.4, 1.4), fill: 'white', opacity: 0.8, stage: 3 },
      { k: 'ink', d: kawaii ? 'M120 134C120 138 117 140 114 139' : 'M120 134C120 140 115 143 110 141', w: 0.8 },
      { k: 'ink', d: M(kawaii ? 'M120 134C120 138 117 140 114 139' : 'M120 134C120 140 115 143 110 141'), w: 0.8 },
    ],
    guides: [
      { d: ellipse(120, 100, 60, 50) },
      { d: poly([[64, 92], [68, 16], [111, 55]]) },
      { d: M(poly([[64, 92], [68, 16], [111, 55]])) },
      { d: poly([[42, 121], [120, 150], [198, 121]], 0, undefined, false) },
      { d: ellipse(120, 178, 46, 42) },
      { d: 'M158 205C206 210 226 180 222 156C219 138 204 122 190 126' },
      { d: ellipse(102, 215, 15, 10) },
      { d: ellipse(138, 215, 15, 10) },
      { d: 'M120 44L120 152', axis: true },
      { d: 'M60 103C90 109 150 109 180 103', axis: true },
    ],
  };
}

/* ----------------------------------- Juno ----------------------------------- */

const J_HAIR = 'M58 118C52 66 82 36 120 36C158 36 188 66 182 118C180 146 172 166 160 178L80 178C68 166 60 146 58 118Z';
const J_BUN = ellipse(120, 30, 22, 20);
const J_FACE = 'M120 54C151 54 170 77 170 108C170 142 149 166 120 166C91 166 70 142 70 108C70 77 89 54 120 54Z';
const J_EAR = ellipse(70, 114, 9, 12);
const J_FRINGE = 'M68 112C63 74 86 48 120 48C154 48 177 70 172 108C164 94 155 82 144 72C131 86 108 94 88 96C79 100 73 105 68 112Z';
const J_NECK = 'M105 152L105 186C112 192 128 192 135 186L135 152Z';
const J_BODY = 'M44 240C46 204 76 182 120 182C164 182 194 204 196 240Z';
const J_COLLAR_L = 'M120 190L98 180L92 196L112 200Z';
const J_STRAP_L = 'M78 194C84 210 88 226 90 240L104 240C102 224 97 206 90 190Z';

export function juno(style: StyleId): FigureDef {
  const kawaii = style === 'C';
  const pencil = (dx: number): string => poly([[84 + dx, 6], [158 + dx, 46], [152 + dx, 57], [78 + dx, 17]], 0);
  return {
    vb: [240, 240],
    layers: [
      { k: 'region', d: J_BODY, fill: 'sweater', sil: true, rim: true, shade: 12 },
      { k: 'region', d: J_STRAP_L, fill: 'overall', clip: J_BODY, stage: 3, shade: 5 },
      { k: 'region', d: M(J_STRAP_L), fill: 'overall', clip: J_BODY, stage: 3, shade: 5 },
      { k: 'spot', d: ellipse(97, 214, 4, 4), fill: 'white', stage: 3 },
      { k: 'spot', d: ellipse(143, 214, 4, 4), fill: 'white', stage: 3 },
      { k: 'region', d: J_HAIR, fill: 'hair', sil: true, shade: 10 },
      // pencil through the bun (behind the bun front)
      { k: 'region', d: pencil(0), fill: 'pencil', sil: true, shade: 3 },
      { k: 'region', d: poly([[158, 46], [170, 56], [152, 57]]), fill: 'pencilWood', sil: true, shade: false },
      { k: 'spot', d: poly([[164.5, 51], [170, 56], [162, 56.5]]), fill: 'line', stage: 3 },
      { k: 'region', d: poly([[84, 6], [78, 17], [70, 13], [76, 2]]), fill: 'eraser', sil: true, shade: false },
      { k: 'region', d: J_BUN, fill: 'hair', sil: true, rim: true, shade: 6 },
      { k: 'ink', d: 'M106 24C112 18 122 16 130 20', w: 0.7, color: 'line', clip: J_BUN },
      { k: 'region', d: J_NECK, fill: 'skin', shade: false },
      { k: 'spot', d: 'M105 152L135 152L135 170C126 176 114 176 105 170Z', fill: 'shade' },
      { k: 'region', d: J_COLLAR_L, fill: 'white', stage: 3, shade: false },
      { k: 'region', d: M(J_COLLAR_L), fill: 'white', stage: 3, shade: false },
      { k: 'region', d: J_EAR, fill: 'skin', sil: true, shade: false },
      { k: 'region', d: M(J_EAR), fill: 'skin', sil: true, shade: false },
      { k: 'region', d: J_FACE, fill: 'skin', sil: true, shade: 10 },
      { k: 'spot', d: ellipse(120, 62, 52, 14), fill: 'shade', clip: J_FACE },
      { k: 'region', d: J_FRINGE, fill: 'hair', sil: true, rim: true, shade: 6 },
      { k: 'ink', d: 'M146 76C152 86 160 95 167 102', w: 0.55, color: 'line' },
      { k: 'ink', d: 'M132 80C124 88 110 92 96 94', w: 0.55, color: 'line' },
      { k: 'ink', d: 'M92 100C97 97 103 97 108 99', w: 1 },
      { k: 'ink', d: M('M92 100C97 97 103 97 108 99'), w: 1 },
      ...eyes(101, kawaii ? 118 : 116, kawaii ? 6.5 : 6.5, kawaii ? 7.5 : 8.5),
      ...eyes(139, kawaii ? 118 : 116, kawaii ? 6.5 : 6.5, kawaii ? 7.5 : 8.5),
      { k: 'spot', d: ellipse(88, 134, kawaii ? 10 : 8, kawaii ? 6 : 5), fill: 'blush', blush: true, opacity: 0.55 },
      { k: 'spot', d: ellipse(152, 134, kawaii ? 10 : 8, kawaii ? 6 : 5), fill: 'blush', blush: true, opacity: 0.55 },
      { k: 'spot', d: 'M152 140c3-3 7-2 8 1 1 4-3 6-6 5-3-1-4-4-2-6z', fill: 'foxTip', opacity: 0.85, stage: 5 },
      { k: 'ink', d: 'M119 120C117 126 117 129 121 130', w: 0.6 },
      { k: 'region', d: kawaii ? 'M112 140C116 147 124 147 128 140C123 142 117 142 112 140Z' : 'M107 138C112 151 128 151 133 138C125 141 115 141 107 138Z', fill: 'mouth', stage: 3, shade: false },
      // round glasses
      { k: 'ink', d: ellipse(101, 116, 15, 14).replace(/Z$/, ''), w: 0.75, taper: 'none', color: 'glasses' },
      { k: 'ink', d: ellipse(139, 116, 15, 14).replace(/Z$/, ''), w: 0.75, taper: 'none', color: 'glasses' },
      { k: 'ink', d: 'M115 113C118 111 122 111 125 113', w: 0.7, taper: 'none', color: 'glasses' },
    ],
    guides: [
      { d: ellipse(120, 108, 50, 57) },
      { d: ellipse(120, 30, 22, 20) },
      { d: 'M44 240C46 204 76 182 120 182C164 182 194 204 196 240' },
      { d: 'M105 152L105 186M135 152L135 186' },
      { d: 'M120 50L120 168', axis: true },
      { d: 'M70 116L170 116', axis: true },
    ],
  };
}
